import * as React from "react";
import EventListener, { withOptions } from "react-event-listener";
import { throttle, flatten } from "lodash";
import Popover from "material-ui/Popover";
import Menu from "material-ui/Menu";
import MaterialMenuItem from "material-ui/MenuItem";
import { Link } from "react-router-dom";
import { BaseComponent } from "components/BaseComponent/BaseComponent";
import ArrowDropRight from "material-ui/svg-icons/navigation-arrow-drop-right";
import {linkMenuClassName} from "uiTestClasses";

export interface MenuItem {
    url: string;
    text: string;
    icon?: JSX.Element;
    exact?: boolean;
}

export interface MenuGroup {
    label: string;
    children: MenuNode[];
}

export interface MenuPopper {
    icon: JSX.Element;
    url: string;
    exact?: boolean;
    onClick: (e: any) => void;
}

export type MenuNode = MenuGroup | MenuItem | MenuPopper;

interface PriorityNavigationProps {
    className: string;
    activeItemClassName: string;
    navigationItems: MenuNode[];
    maxNavigationItems: number;
    showHamburgerIcon?: boolean;
}

interface PriorityNavigationState {
    priorityItems: MenuNode[];
    moreItems: MenuNode[];
    isMoreMenuOpen: boolean;
    hasMeasurements: boolean;
    availableWidth?: number;
}

class PriorityNavigation extends BaseComponent<PriorityNavigationProps, PriorityNavigationState> {
    widthsArray: number[] = [];
    navigation: HTMLElement;
    onResizeThrottled: any;
    containerElement: HTMLElement;
    lastKnownContainerWidth: number;

    constructor(props: PriorityNavigationProps) {
        super(props);

        this.updateNavigation = this.updateNavigation.bind(this);
        this.howManyItemsInMenuArray = this.howManyItemsInMenuArray.bind(this);
        this.onResizeThrottled = throttle(this.onResize, 100);

        this.state = {
            hasMeasurements: false,
            isMoreMenuOpen: false,
            priorityItems: this.props.navigationItems,
            moreItems: [],
            availableWidth: null
        };
    }

    componentDidMount() {
        //Get width of all items in navigation menu
        this.widthsArray = Array.prototype.slice.call(this.navigation.children).map((item: HTMLElement) => item.getBoundingClientRect().width);
        this.setState({ hasMeasurements: true }, () => this.updateNavigation(this.props));
    }

    componentWillReceiveProps(nextProps: PriorityNavigationProps) {
        this.updateNavigation(nextProps);
    }

    render() {
        const { priorityItems, moreItems } = this.state;

        // Do first pass to measure all items
        if (!this.state.hasMeasurements) {
            return <nav className={this.props.className} role="navigation">
                <ul ref={this.setNavigation}>
                    {priorityItems.map((item: any, index: number) => <NavItem key={`navItem-${index}`} item={item} activeItemClassName={this.props.activeItemClassName} />)}
                    <MoreButton activeItemClassName={this.props.activeItemClassName} moreItems={this.state.moreItems} />
                </ul>
            </nav>;
        }

        // Measure the container to find out available width
        if (this.state.availableWidth === null) {
            // This div will live in the document for a brief moment, just long
            // enough for it to mount. We then use it to calculate its width, and
            // replace it immediately with the underlying component.
            return <div
                style={{ flexGrow: 1, width: "100%" }}
                ref={this.setMeasurementDiv}
            />;
        }

        return (
            <nav className={this.props.className} role="navigation">
                <EventListener
                    target="window"
                    onResize={withOptions(this.onResizeThrottled, { passive: true })}
                />
                <ul>
                    {priorityItems.map((item: MenuNode, index: number) => <NavItem key={`navItem-${index}`} item={item} activeItemClassName={this.props.activeItemClassName} />)}
                    {moreItems.length > 0 && <MoreButton activeItemClassName={this.props.activeItemClassName}
                        icon={this.props.showHamburgerIcon && priorityItems.length === 0 ? "fa fa-bars" : null}
                        moreItems={moreItems} />}
                </ul>
            </nav>
        );
    }

    private howManyItemsInMenuArray(array: number[], outerWidth: number, initialWidth: number, maximumNumberInNav: number) {
        let total = initialWidth;
        for (let i = 0; i < array.length; i++) {
            if (i > maximumNumberInNav) {
                return maximumNumberInNav;
            }

            total += array[i];

            if (total > outerWidth) {
                return i;
            }
        }

        return array.length;
    }

    private updateNavigation(props: PriorityNavigationProps) {
        if (!(this.state.hasMeasurements && this.state.availableWidth !== null)) {
            return;
        }

        const arrayAmount = this.howManyItemsInMenuArray(this.widthsArray, this.state.availableWidth, this.widthsArray[this.widthsArray.length - 1], props.maxNavigationItems);
        const navItemsCopy = props.navigationItems;
        const priorityItems = navItemsCopy.slice(0, arrayAmount);

        this.setState({
            priorityItems,
            moreItems: priorityItems.length !== navItemsCopy.length ? navItemsCopy.slice(arrayAmount, navItemsCopy.length).filter(item => !isMenuPopper(item)) : []
        });
    }

    private onResize = () => {
        if (this.containerElement.offsetWidth === this.lastKnownContainerWidth) {
            return; // Nothing changed
        }

        this.setState({ availableWidth: null });
    }

    private showMoreMenu = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        this.setState({
            isMoreMenuOpen: !this.state.isMoreMenuOpen
        });
    }

    private hideMoreMenu = () => {
        this.setState({
            isMoreMenuOpen: false
        });
    }

    private setMeasurementDiv = (el: any) => {
        if (!el) {
            return;
        }
        this.containerElement = el.parentNode;
        this.lastKnownContainerWidth = this.containerElement.offsetWidth;

        this.setState({
            availableWidth: el.offsetWidth
        }, () => this.updateNavigation(this.props));
    }

    private setNavigation = (el: any) => {
        this.navigation = el;
    }
}

interface MoreButtonProps {
    icon?: string;
    moreItems: MenuNode[];
    activeItemClassName: string;
}

const MoreButton: React.StatelessComponent<MoreButtonProps> = props => <li key="navItem-more">
    <LinksMenu items={props.moreItems} label="More" icon={props.icon} />
</li>;

interface NavItemProps {
    item: MenuNode;
    activeItemClassName: string;
}

class NavItem extends React.Component<NavItemProps> {

    render() {
        const item = this.props.item;

        if (isMenuGroup(item)) {
            const isActive = hasActiveDescendants(item);
            return <li>
                <LinksMenu activeItemClassName={isActive ? this.props.activeItemClassName : null} items={item.children} label={item.label} />
            </li>;
        }
        if (isMenuPopper(item)) {
            return <MenuPopperLi icon={item.icon} class={isActiveUrl(item.url, false) ? this.props.activeItemClassName : null}
                onClick={(el: any) => this.menuPopperClicked(item, el)} />;
        }
        return <li>
            <Link className={isActiveUrl(item.url, item.exact) ? this.props.activeItemClassName : null} to={item.url}>
                {item.icon}
                <span>{item.text}</span>
            </Link>
        </li>;
    }

    private menuPopperClicked = (item: any, el: any) => {
        item.onClick(el);
    }
}

interface MenuPopperLiProps {
    icon: JSX.Element;
    class: string;
    onClick: (e: any) => void;
}

class MenuPopperLi extends React.Component<MenuPopperLiProps> {
    anchorEl: HTMLElement;

    render() {
        return <li ref={this.setRef}>
            <a className={this.props.class} onClick={this.onClick} href="#">
                {this.props.icon}
            </a>
        </li>;
    }

    private onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        this.props.onClick(this.anchorEl);
    }

    private setRef = (el: HTMLElement) => {
        this.anchorEl = el;
    }
}

interface LinksMenuProps {
    items: MenuNode[];
    label?: string;
    icon?: string;
    activeItemClassName?: string;
}

interface LinksMenuState {
    isOpen: boolean;
    anchor?: HTMLAnchorElement;
}

class LinksMenu extends React.Component<LinksMenuProps, LinksMenuState> {
    constructor(props: LinksMenuProps) {
        super(props);
        this.state = { isOpen: false };
    }

    render() {
        return <a onClick={e => this.openMenu(e)} href="#" className={this.props.activeItemClassName}>
            {this.props.icon && <i className={this.props.icon} />}
            {!this.props.icon && <span>{this.props.label}</span>}
            {!this.props.icon && <i className="fa fa-caret-down" />}
            <Popover
                className={linkMenuClassName}
                open={this.state.isOpen}
                anchorEl={this.state.anchor}
                onRequestClose={() => this.closeMenu()}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                targetOrigin={{ horizontal: "right", vertical: "top" }}>
                <Menu>
                    {this.props.items.map((item, i) =>
                        <MenuNodeComponent node={item} key={i} onClick={this.closeMenu} />)}
                </Menu>
            </Popover>
        </a>;
    }

    private closeMenu = () => {
        this.setState({ isOpen: false, anchor: null });
    }

    private openMenu(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();

        const anchor = event.currentTarget;
        this.setState({ isOpen: true, anchor });
    }
}

interface MenuNodeComponentProps {
    node: MenuNode;
    onClick: () => void;
}

const MenuNodeComponent: React.SFC<MenuNodeComponentProps> = (props: MenuNodeComponentProps) => {
    const activeStyle = { color: "#2f93e0" };

    if (isMenuGroup(props.node)) {
        return <MaterialMenuItem
            style={hasActiveDescendants(props.node) ? activeStyle : {}}
            primaryText={props.node.label}
            rightIcon={<ArrowDropRight />}
            menuItems={props.node.children.map((c, index) => <MenuNodeComponent node={c} key={index} onClick={props.onClick} />)}
        />;
    }

    if (isMenuPopper(props.node)) {
        return <Link to={props.node.url}>
            <MaterialMenuItem
                leftIcon={props.node.icon}
                onClick={() => props.onClick()}
            />
        </Link >;
    }

    return <Link to={props.node.url}>
        <MaterialMenuItem
            style={isActiveUrl(props.node.url, props.node.exact) ? activeStyle : {}}
            primaryText={props.node.text}
            onClick={() => props.onClick()}
        />
    </Link >;
};

function isMenuGroup(node: MenuNode): node is MenuGroup {
    return (node as MenuGroup).children !== undefined;
}

function isMenuPopper(node: MenuNode): node is MenuPopper {
    return (node as MenuPopper).onClick !== undefined;
}

function hasActiveDescendants(group: MenuGroup): boolean {
    const allDescendantLinks = (_: MenuGroup): Array<MenuItem | MenuPopper> => flatten(_.children.map((c: MenuNode) => {
        if (isMenuGroup(c)) {
            return allDescendantLinks(c);
        }
        return [c];
    }));

    return allDescendantLinks(group).some(l => isActiveUrl(l.url, l.exact));
}

function isActiveUrl(itemUrl: string, exact: boolean) {
    let isActive = false;
    const routeStartIndex = window.location.href.indexOf("#/");

    if (routeStartIndex !== -1) {
        const routePath = window.location.href.substr(routeStartIndex + 1);
        isActive = exact ? routePath === itemUrl : routePath.startsWith(itemUrl);
    }

    return isActive;
}

export default PriorityNavigation;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/PriorityNavigation/PriorityNavigation.tsx